// =============================================================================
// features/sales/components/useFocusTrap.ts — Foco preso dentro de um painel
// flutuante (drawer/diálogo), com devolução ao elemento anterior ao fechar.
//
// Compartilhado por AppointmentDrawer (agenda) e ScheduleAppointmentDialog
// (reuso fora da agenda) — nenhum dos dois reimplementa a lógica (F33-S06,
// doc 26 §7 "foco, leitores de tela, contraste e navegação por teclado
// cobertos em testes").
//
// Comportamento:
//   - Ao ativar (`active: true`): guarda `document.activeElement`, move o
//     foco para o primeiro elemento focável do container (ou o próprio
//     container quando não há nenhum).
//   - Tab/Shift+Tab ciclam DENTRO do container — nunca escapam para o
//     documento por trás (WCAG 2.4.3, "trapped focus" de diálogo modal).
//   - Escape chama `onEscape` — a decisão de bloquear (guarda de edição) é
//     do chamador, não deste hook.
//   - Ao desativar/desmontar: devolve o foco ao elemento salvo, se ele ainda
//     estiver no DOM (evita `focus()` em nó removido).
// =============================================================================

import * as React from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

export interface UseFocusTrapOptions {
  /** Container preso ao foco. Só age quando `active` é true e o ref já resolveu. */
  containerRef: React.RefObject<HTMLElement>;
  /** Liga/desliga a armadilha — normalmente `true` enquanto o painel está aberto. */
  active: boolean;
  /** Chamado quando o usuário pressiona Escape. Guarda de edição é decisão do chamador. */
  onEscape?: () => void;
}

export function useFocusTrap({ containerRef, active, onEscape }: UseFocusTrapOptions): void {
  const previouslyFocused = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!active) return undefined;

    const maybeContainer = containerRef.current;
    if (!maybeContainer) return undefined;
    const container: HTMLElement = maybeContainer;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Move o foco para dentro do painel no próximo tick (o painel acabou de montar/animar).
    const focusables = getFocusable(container);
    const target = focusables[0] ?? container;
    if (target === container && container.tabIndex !== 0) {
      container.tabIndex = -1;
    }
    target.focus();

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        onEscape?.();
        return;
      }

      if (e.key !== 'Tab') return;
      const current = getFocusable(container);
      if (current.length === 0) {
        e.preventDefault();
        return;
      }

      const first = current[0];
      const last = current[current.length - 1];
      if (!first || !last) return;

      const activeEl = document.activeElement;

      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last || !container.contains(activeEl)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const toRestore = previouslyFocused.current;
      if (toRestore && document.contains(toRestore)) {
        toRestore.focus();
      }
    };
    // containerRef é estável (ref) — não entra na lista de dependências.
  }, [active, onEscape]);
}
